import type { LucideIcon } from 'lucide-react'
import { MoreHorizontalIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { InlineEditableText } from '@/components/atoms/inline-editable-text.atom'
import { DropdownMenu, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { SidebarMenuAction, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'

import { SidebarAgentActionsMenu } from './sidebar-agent-actions-menu.molecule'
import { SidebarChatActionsMenu } from './sidebar-chat-actions-menu.molecule'
import type {
  AgentItemActions,
  ChatItemActions,
  ChatItemEditState,
  ProjectItemActions,
} from './sidebar-item-actions.interface'
import { SidebarProjectActionsMenu } from './sidebar-project-actions-menu.molecule'

interface BaseItemProps {
  id: string

  title: string

  href: string

  icon: LucideIcon

  isActive?: boolean

  editState?: ChatItemEditState
}

interface ChatItemProps extends BaseItemProps {
  itemType: 'chat'

  isPinned?: boolean

  actions: ChatItemActions
}

interface ProjectItemProps extends BaseItemProps {
  itemType: 'project'

  actions: ProjectItemActions
}

interface AgentItemProps extends BaseItemProps {
  itemType: 'agent'

  isPinned?: boolean

  actions: AgentItemActions
}

type SidebarNavItemWithMenuProps = ChatItemProps | ProjectItemProps | AgentItemProps

const renderActionsMenu = (props: SidebarNavItemWithMenuProps): ReactNode => {
  if (props.itemType === 'chat') {
    return (
      <SidebarChatActionsMenu chatId={props.id} isPinned={props.isPinned} actions={props.actions} />
    )
  }

  if (props.itemType === 'project') {
    return <SidebarProjectActionsMenu projectId={props.id} actions={props.actions} />
  }

  return (
    <SidebarAgentActionsMenu agentId={props.id} isPinned={props.isPinned} actions={props.actions} />
  )
}

export const SidebarNavItemWithMenu = (props: SidebarNavItemWithMenuProps): ReactNode => {
  const { id, title, href, icon: Icon, isActive, editState } = props
  const isEditing = props.itemType === 'chat' && editState?.editingId === id
  const isRenaming = editState?.isRenaming ?? false

  if (isEditing && props.itemType === 'chat') {
    return (
      <SidebarMenuItem key={id}>
        <div className="sidebar-nav-item-editing flex items-center gap-2 px-2 py-1">
          <Icon className="size-4 shrink-0" />
          <InlineEditableText
            value={title}
            isEditing={true}
            isLoading={isRenaming}
            onSave={(newName) => props.actions.onRenameSave?.(id, newName)}
            onCancel={() => props.actions.onRenameCancel?.()}
            className="flex-1"
            inputClassName="h-6 text-sm"
          />
        </div>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem key={id}>
      <SidebarMenuButton asChild isActive={isActive} tooltip={title}>
        <Link to={href}>
          <Icon />
          <span>{title}</span>
        </Link>
      </SidebarMenuButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction showOnHover>
            <MoreHorizontalIcon />
            <span className="sr-only">More options</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        {renderActionsMenu(props)}
      </DropdownMenu>
    </SidebarMenuItem>
  )
}
